// material-ui
import { Grid } from '@material-ui/core';
import React from 'react';

// api
import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { ICountryGroup } from '~/src/dash/api/console-api';
import { getCountryGroupsByPublisherID } from '~/src/dash/api/country-group';
import {
  DestinationLocalTargetingParams,
  DestinationMenuLists,
  DestinationTypeRoute,
  DestinationTypeSite,
  genInvenRouteChannelListFromSelected,
  genInvenRouteRouteListFromSelected,
  getInventoryRouteByID,
  getInventoryRoutesByPublisherID,
  IInventoryRoute,
} from '~/src/dash/api/inventory_routes';
// other
import { ILocalTargetingParameter } from '~/src/dash/components/local-targeting/local-targeting';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  navigate,
  parseLocation,
  RouteInventoryRouteList,
} from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import {
  genCountriesMenuListFromSelectedAndDisable,
  genTargetingCountryGroupListFromSelectedAndDisable,
} from '~/src/dash/utils/countries';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { importLocalTargetingParamFromJSON } from '~/src/dash/utils/params';
import { IPermissionCheck, IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
// views
import { LoadingView } from '~/src/dash/views/loading-view';
import { RouteSettingsView } from '~/src/dash/views/route/route-settings-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  currentRoute?: IInventoryRoute;
  snackbarMessage: string;
  snackbarError: boolean;
  loaded: boolean;
  publisherCountryGroups: ICountryGroup[];
  inventoryRoutesForPublisher: IInventoryRoute[];
  sites: ISmallSite[];
}

export const RouteSettingsExistingView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      currentRoute: undefined,
      snackbarMessage: '',
      snackbarError: false,
      loaded: false,
      publisherCountryGroups: [] as ICountryGroup[],
      inventoryRoutesForPublisher: [] as IInventoryRoute[],
      sites: [] as ISmallSite[],
    };

    componentDidMount() {
      this.getSites();
      const { session } = this.props;
      Promise.all([
        getCountryGroupsByPublisherID(session.publisher.ID),
        getInventoryRoutesByPublisherID(this.props.session.publisher.ID),
      ]).then((results) => {
        const publisherCountryGroups: ICountryGroup[] = results[0];
        const inventoryRoutes: IInventoryRoute[] = results[1];
        this.setState(
          {
            publisherCountryGroups,
            inventoryRoutesForPublisher: inventoryRoutes.filter(
              (route) =>
                route.ID.toString() !== this.props.location.pathname.split('inventory-routes/')[1],
            ),
          },
          () => this.getInventoryRoute(),
        );
      });
    }

    hasWriteRights = (): IPermissionCheck => {
      return IS_AUTHORIZED(['write_inventory_route'], this.props.user.user);
    };

    // if someone manually changes the url or is redirected, reload route
    componentDidUpdate(prevProps: Readonly<IProps>) {
      if (prevProps.location.pathname !== this.props.location.pathname) {
        this.getInventoryRoute();
      }
    }

    getSites = async () => {
      const [smallSites, err] = await getSmallSites(this.props.session.publisher.ID, 'uuid');
      if (err) {
        this.setState({
          snackbarError: true,
          snackbarMessage: 'Error loading sites',
        });
        return;
      }
      const sites: ISmallSite[] = smallSites || [];
      this.setState({
        sites,
      });
    };

    getInventoryRoute(): void {
      const { publisherCountryGroups, inventoryRoutesForPublisher } = this.state;
      const loc = parseLocation(this.props.location.pathname);

      if (loc.inventoryrouteid) {
        getInventoryRouteByID(this.props.session.publisher.ID, loc.inventoryrouteid)
          .then((currentRoute: IInventoryRoute) => {
            const Destinations = currentRoute.Destinations;
            for (let i = 0; i < Destinations.length; i++) {
              Destinations[i]['targetingCountriesList' as DestinationMenuLists] =
                genCountriesMenuListFromSelectedAndDisable(
                  Destinations[i].TargetingDeviceCountry,
                ) as IMenuList;
              Destinations[i]['targetingCountryGroupList' as DestinationMenuLists] =
                genTargetingCountryGroupListFromSelectedAndDisable(
                  publisherCountryGroups,
                  Destinations[i].TargetingCountryGroupIDs,
                ) as IMenuList;
              Destinations[i]['localTargetingParameters' as DestinationLocalTargetingParams] =
                importLocalTargetingParamFromJSON(
                  Destinations[i].LocalTargeting,
                ) as ILocalTargetingParameter[];
              if (Destinations[i].DestinationType === DestinationTypeSite) {
                Destinations[i]['inventoryRouteChannelList' as DestinationMenuLists] =
                  genInvenRouteChannelListFromSelected(
                    Destinations[i].SiteID,
                    this.state.sites,
                    this.props.user.user.Username,
                  ) as IMenuList;
                Destinations[i]['inventoryRouteRouteList' as DestinationMenuLists] =
                  {} as IMenuList;
              } else if (Destinations[i].DestinationType === DestinationTypeRoute) {
                Destinations[i]['inventoryRouteRouteList' as DestinationMenuLists] =
                  genInvenRouteRouteListFromSelected(
                    Destinations[i].ForwardingRouteID,
                    inventoryRoutesForPublisher,
                    this.props.user.user.Username,
                  ) as IMenuList;
                Destinations[i]['inventoryRouteChannelList' as DestinationMenuLists] =
                  {} as IMenuList;
              } else {
                Destinations[i]['inventoryRouteChannelList' as DestinationMenuLists] =
                  {} as IMenuList;
                Destinations[i]['inventoryRouteRouteList' as DestinationMenuLists] =
                  {} as IMenuList;
              }
            }
            this.setState({
              currentRoute,
              loaded: true,
            });
          })
          .catch(() => {
            this.setState(
              {
                snackbarMessage:
                  "You don't have access to this resource or it does not exist. Please create a new one or select one from the list.",
                snackbarError: true,
                loaded: false,
              },
              () => {
                setTimeout(() => {
                  navigate(
                    RouteInventoryRouteList,
                    extractRouteParams({
                      session: this.props.session,
                      user: this.props.user,
                    }),
                  );
                }, 3000);
              },
            );
          });
      }
    }

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    render() {
      const {
        loaded,
        currentRoute,
        snackbarMessage,
        snackbarError,
        publisherCountryGroups,
        inventoryRoutesForPublisher,
      } = this.state;

      if (loaded) {
        return (
          <Grid>
            <RouteSettingsView
              currentRoute={currentRoute}
              readOnly={!this.hasWriteRights().isAuthorized}
              disableAppBar={false}
              publisherCountryGroups={publisherCountryGroups}
              inventoryRoutesForPublisher={inventoryRoutesForPublisher}
            />
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Grid>
        );
      } else {
        return <LoadingView active={true} text='Loading the route settings' />;
      }
    }
  },
);
